import {
  type CountryCode,
  getCountryCallingCode,
  type PhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/min';

import { REGIONAL_DEFAULTS } from '../contract/regional';
import type { PhoneStyle, PreferenceValues } from '../contract/types';

/**
 * Phone numbers: reading a stored one, checking it, and showing it.
 *
 * Built on `libphonenumber-js` with its min metadata, a peer dependency the host app supplies (R1 in
 * the README). Laying out any country's numbers takes the numbering-plan dataset, which moves with
 * national regulators and is not something to hand-write.
 *
 * Every function here, bar the legacy section at the end, reads a stored value through
 * {@link parseStored}, so a page and the form editing the same record cannot disagree about which
 * number it holds.
 */

/** The mark opening a number written with its country code, as E.164 always is. */
const PHONE_PLUS_SIGN = '+';

/** A number stored without a country code is read as this country's, as the backend migration does. */
const FALLBACK_COUNTRY = REGIONAL_DEFAULTS.country;

/** More digits than a North American national number, so they may open with a country code. */
const MIN_DIGITS_WITH_CODE = 11;

/**
 * A stored value as a phone number, or `undefined` when it cannot be read as one.
 *
 * Records predate the `+`, so a value with no mark and more digits than a national number, invalid
 * under the fallback country but valid with a `+` in front, is read as international:
 * `525512345678` is Mexican. A `+` anywhere but the front names no country, so such a value is
 * unreadable rather than guessed at.
 *
 * A value with an extension is unreadable too: E.164 has no room for one, so reading it as a number
 * would drop the extension from whatever is shown or saved. So is a number with other text around
 * it, which reading would drop the same way.
 */
const parseStored = (raw: string): PhoneNumber | undefined => {
  const value = raw.trim();

  if (value.slice(1).includes(PHONE_PLUS_SIGN)) return undefined;

  const parsed = parsePhoneNumberFromString(value, {
    defaultCountry: FALLBACK_COUNTRY,
    extract: false,
  });

  if (parsed?.ext) return undefined;

  if (parsed?.isValid() || value.startsWith(PHONE_PLUS_SIGN)) return parsed;

  if (value.replace(/\D/g, '').length < MIN_DIGITS_WITH_CODE) return parsed;

  const international = parsePhoneNumberFromString(
    `${PHONE_PLUS_SIGN}${value}`,
    { extract: false }
  );

  return international?.isValid() ? international : parsed;
};

/**
 * A phone number in the one shape it is saved in, E.164, whatever punctuation it arrived in.
 *
 * An invalid number still has that shape (`5551234567` is `+15551234567`), which is what lets an
 * editor open on its digits so the reader can correct them.
 *
 * @param raw - The number as stored or typed, in any punctuation.
 * @returns The E.164 string, or `undefined` when there is none or it cannot be read as a number.
 */
export const normalizePhoneNumber = (
  raw: string | null | undefined
): string | undefined => (raw ? parseStored(raw)?.number : undefined);

/**
 * Whether a stored value is a real, dialable number.
 *
 * @param raw - The number as stored, in any punctuation.
 * @returns `true` only for a number the numbering plan says can exist.
 */
export const isValidPhone = (raw: string): boolean =>
  parseStored(raw)?.isValid() ?? false;

/**
 * The country a valid stored number belongs to.
 *
 * Countries sharing a dial code can resolve to a neighbour: a `+44` mobile may come back `GG`.
 *
 * @param raw - The number as stored, in any punctuation.
 * @returns The ISO country, or `undefined` for an invalid or unreadable value.
 */
export const phoneCountry = (raw: string): CountryCode | undefined => {
  const parsed = parseStored(raw);

  return parsed?.isValid() ? parsed.country : undefined;
};

/**
 * Formats a phone number for display.
 *
 * A number on the reader's own calling code is written nationally, so it reads the same whether or
 * not the record kept the code. Any other is written internationally, its code saying where it is
 * from. A value that is not a valid number is returned exactly as stored: formatting it would strip
 * its punctuation and pass it off as a number nobody can dial.
 *
 * @param preferences - Supplies the reader's own country.
 * @param value - The number, in whatever punctuation it was stored with.
 * @returns The formatted number, or the input unchanged.
 */
export const formatPhone = (
  preferences: PreferenceValues,
  value: string
): string => {
  const parsed = parseStored(value);

  if (!parsed?.isValid()) return value;

  return parsed.countryCallingCode ===
    getCountryCallingCode(preferences.formats.phone)
    ? parsed.formatNational()
    : parsed.formatInternational();
};

/** What a phone field needs from the reader beyond what `format.phone` shows. */
export type PhoneBundle = {
  /**
   * The reader's own country, for a phone field to start on.
   *
   * Read from the style that decides the layout, so the country a field opens on is the one the
   * page behind it formats for.
   */
  readonly ownCountry: PhoneStyle;
};

/**
 * Assembles the phone bundle for a reader.
 *
 * @param preferences - Supplies the reader's own country.
 * @returns The bundle. See {@link PhoneBundle}.
 */
export const buildPhoneBundle = (
  preferences: PreferenceValues
): PhoneBundle => ({
  ownCountry: preferences.formats.phone,
});

// TODO: Remove everything below with `fleet-management`'s `phone.utils.ts`, its only reader.
// Those pre-core helpers serve the localization flag off; nothing in this package uses these.

/** The digits a number holds once any country code is off the front, in every market served. */
const NATIONAL_DIGITS = 10;

/** The code each style's own country is reached on, which the US and Canada share. */
const OWN_COUNTRY_CODE: Readonly<Record<PhoneStyle, string>> = {
  US: '1',
  CA: '1',
  MX: '52',
};

/** The country codes served, longest first, so a short code cannot claim a longer one's number. */
const COUNTRY_CODES: readonly string[] = ['52', '1'];

/** How many digits a number holds once its country code is off the front, in every market served. */
export const SERVED_NATIONAL_DIGITS = NATIONAL_DIGITS;

/** The code each style's own country is reached on, for callers naming one country in particular. */
export const COUNTRY_CODE_BY_STYLE = OWN_COUNTRY_CODE;

/** Whether a `+` sits anywhere but the front, where it names nothing. */
const misplacedMark = (value: string): boolean => {
  const trimmed = value.trimStart();

  return trimmed
    .slice(trimmed.startsWith(PHONE_PLUS_SIGN) ? 1 : 0)
    .includes(PHONE_PLUS_SIGN);
};

/**
 * The country code a stored number carries, reading digits alone.
 *
 * The `+` is not required: a number stored before anyone wrote one is still a number from
 * somewhere. A code only counts when a whole national number follows it, and a misplaced mark
 * names no country.
 *
 * @param phoneNumber - The number as stored, punctuated or not.
 * @returns The code, or an empty string when the digits carry none.
 */
export const storedCountryCode = (phoneNumber: string): string => {
  if (misplacedMark(phoneNumber)) return '';

  const digits = phoneNumber.replace(/\D/g, '');

  return (
    COUNTRY_CODES.find(
      (code) =>
        digits.startsWith(code) &&
        digits.length === code.length + NATIONAL_DIGITS
    ) ?? ''
  );
};
